import type { PublicMatchMode } from '../config/contract';
import { BOOST_MAX, BOOST_SMALL } from './boost';
import type { CheckpointMarker } from '../tracks/types';

export type RaceStatus = 'countdown' | 'racing' | 'finished';

const SMALL_COOLDOWN = 3.5;
const BIG_COOLDOWN = 8;

export interface BoostGrant {
  index: number;
  amount: number;
  pad: 'small' | 'big';
}

export class RaceSession {
  status: RaceStatus = 'countdown';
  countdown = 2.8;
  completedLaps = 0;
  nextCheckpoint = 1;
  raceTime = 0;
  lapTime = 0;
  bestLapMs: number | null = null;
  finishTime = 0;
  message = '';
  messageUntil = 0;
  offTrackTime = 0;
  collisionFlash = 0;
  matchMode: PublicMatchMode;
  private armedFinish = false;
  private sectorStamp = 0;
  private readonly checkpointCount: number;
  lastSafeIndex = 0;
  pendingBeep: string | null = null;
  pendingLapMs: number | null = null;
  private grants: BoostGrant[] = [];
  private readonly cooldownUntil = new Map<number, number>();
  private splineReady = false;
  private lastSpline = 0;
  private lapTravel = 0;

  constructor(
    readonly totalLaps: number,
    readonly lapLength: number,
    private readonly checkpoints: CheckpointMarker[],
    matchMode: PublicMatchMode = 'checkpointsAsNitro',
  ) {
    this.checkpointCount = checkpoints.length;
    this.matchMode = matchMode;
  }

  setMatchMode(mode: PublicMatchMode): void {
    this.matchMode = mode;
  }

  get displayLap(): number {
    if (this.status === 'finished') return this.totalLaps;
    return Math.min(this.totalLaps, this.completedLaps + 1);
  }

  get countdownLabel(): string | null {
    if (this.status === 'countdown') {
      if (this.countdown > 2.1) return '3';
      if (this.countdown > 1.4) return '2';
      if (this.countdown > 0.7) return '1';
      return 'VAI!';
    }
    if (this.status === 'racing' && this.raceTime < 0.45) return 'VAI!';
    return null;
  }

  /** Spawner pads dim until the cooldown ends. Checkpoint mode keeps them lit. */
  padGlow(index: number): boolean {
    if (this.matchMode !== 'nitroSpawnersOnly') return true;
    return this.raceTime >= (this.cooldownUntil.get(index) ?? 0);
  }

  consumeBoostGrants(): BoostGrant[] {
    const grants = this.grants;
    this.grants = [];
    return grants;
  }

  skipCountdown(): void {
    if (this.status !== 'countdown') return;
    this.status = 'racing';
    this.countdown = 0;
    this.raceTime = 0;
    this.lapTime = 0;
    this.sectorStamp = 0;
    this.splineReady = false;
    this.lapTravel = 0;
  }

  update(dt: number, overlaps: Set<number>, splineDistance: number, onTrack: boolean, y: number): 'respawn' | 'finished' | null {
    this.messageUntil -= dt;
    if (this.messageUntil <= 0 && this.status === 'racing' && onTrack) this.message = '';
    this.collisionFlash = Math.max(0, this.collisionFlash - dt);

    if (this.status === 'countdown') {
      const previous = this.countdownLabel;
      this.countdown -= dt;
      const next = this.countdownLabel;
      if (next && next !== previous && next !== 'VAI!') this.pendingBeep = next;
      if (this.countdown <= 0) {
        this.status = 'racing';
        this.raceTime = 0;
        this.lapTime = 0;
        this.sectorStamp = 0;
        this.splineReady = false;
        this.lapTravel = 0;
        this.pendingBeep = 'go';
      }
      return null;
    }

    if (this.status !== 'racing') return null;
    this.raceTime += dt;
    this.lapTime += dt;

    if (this.matchMode === 'nitroSpawnersOnly') {
      this.trackTravel(splineDistance);
      for (const index of overlaps) this.trySpawner(index);
    } else {
      if (!overlaps.has(0)) this.armedFinish = true;
      for (const index of overlaps) this.tryCheckpoint(index);
    }

    const finished = (this.status as RaceStatus) === 'finished';
    if (!onTrack && !finished) {
      this.offTrackTime += dt;
      this.setMessage('Fora da pista — pressione R para recuperar', 0.4);
      if (y < -2.5 || this.offTrackTime > 2.1) return 'respawn';
    } else {
      this.offTrackTime = 0;
    }

    return finished ? 'finished' : null;
  }

  consumeBeep(): string | null {
    const beep = this.pendingBeep;
    this.pendingBeep = null;
    return beep;
  }

  consumeLap(): number | null {
    const lap = this.pendingLapMs;
    this.pendingLapMs = null;
    return lap;
  }

  noteCollision(): void {
    this.collisionFlash = 0.35;
    this.setMessage('Colisão!', 0.8);
  }

  noteRespawn(): void {
    this.offTrackTime = 0;
    this.setMessage('Posição recuperada', 1.6);
  }

  rankingDistance(splineDistance: number): number {
    let distance = splineDistance;
    if (this.nextCheckpoint === 0 && splineDistance < 40) distance += this.lapLength;
    return this.completedLaps * this.lapLength + distance;
  }

  private tryCheckpoint(index: number): void {
    if (index !== this.nextCheckpoint) return;
    if (index === 0 && !this.armedFinish) return;
    const marker = this.checkpoints[index];
    if (!marker) return;
    // The pad sensor is short, so overlap means the cart is on that pad.
    // The finish pad still waits until the cart has left it once, so spawning
    // on the line does not close a lap.

    const sector = this.raceTime - this.sectorStamp;
    this.sectorStamp = this.raceTime;
    this.lastSafeIndex = index;
    this.grant(marker);

    if (index === 0) {
      this.closeLap();
      return;
    }

    this.nextCheckpoint = (index + 1) % this.checkpointCount;
    this.setMessage(`Setor ${index} · ${sector.toFixed(2)}s`, 1.6);
  }

  private trySpawner(index: number): void {
    const marker = this.checkpoints[index];
    if (!marker) return;
    this.lastSafeIndex = index;
    const readyAt = this.cooldownUntil.get(index) ?? 0;
    if (this.raceTime < readyAt) return;
    this.cooldownUntil.set(index, this.raceTime + (marker.pad === 'big' ? BIG_COOLDOWN : SMALL_COOLDOWN));
    this.grant(marker);
    if (marker.pad === 'big') this.setMessage('Nitro!', 0.8);
  }

  /** Forward distance along the spline. Large jumps (teleports) do not count as a lap. */
  private trackTravel(splineDistance: number): void {
    if (!this.splineReady) {
      this.lastSpline = splineDistance;
      this.splineReady = true;
      this.lapTravel = 0;
      return;
    }
    let delta = splineDistance - this.lastSpline;
    if (delta > this.lapLength * 0.5) delta -= this.lapLength;
    else if (delta < -this.lapLength * 0.5) delta += this.lapLength;
    this.lastSpline = splineDistance;
    if (delta <= 0 || delta > 12) return;
    this.lapTravel += delta;
    if (this.lapTravel >= this.lapLength && this.status === 'racing') {
      this.lapTravel -= this.lapLength;
      this.closeLap();
    }
  }

  private closeLap(): void {
    const lapMs = this.lapTime * 1000;
    if (this.bestLapMs == null || lapMs < this.bestLapMs) this.bestLapMs = lapMs;
    this.completedLaps += 1;
    this.pendingLapMs = lapMs;
    if (this.completedLaps >= this.totalLaps) {
      this.status = 'finished';
      this.finishTime = this.raceTime;
      this.setMessage('Chegada!', 6);
      return;
    }
    this.lapTime = 0;
    this.nextCheckpoint = 1;
    this.armedFinish = false;
    this.setMessage(`Volta ${this.completedLaps} concluída`, 2.2);
  }

  private grant(marker: CheckpointMarker): void {
    if (marker.pad === 'big') {
      this.grants.push({ index: marker.index, amount: BOOST_MAX, pad: 'big' });
      return;
    }
    this.grants.push({ index: marker.index, amount: BOOST_SMALL, pad: 'small' });
  }

  private setMessage(text: string, seconds: number): void {
    if (this.messageUntil > 0.25 && this.message !== text && this.message !== 'Fora da pista — pressione R para recuperar') return;
    this.message = text;
    this.messageUntil = seconds;
  }
}
